/* eslint-disable @typescript-eslint/no-unused-vars */
import { FC, useMemo } from 'react'
import { useFormik } from 'formik'
import { object, string } from 'yup'
import { useMutation } from '@apollo/client'

import {
  SubmitForGiveawayMutation,
  SubmitForGiveawayMutationVariables,
  AuctionByArtworkQuery,
  UserOffersQuery,
} from 'gql'
import { Button, Typography, Input, Grid, Link, Checkbox } from '~ui'
import { useQueryParams } from '~hooks'
import { getIdFromSlug } from '~utils'
import { EMAIL } from 'constants/schema'
import { SUBMIT_FOR_GIVEAWAY } from 'gql/mutations'
import getUserBiddingStatusOnArtwork from '../bid-helpers'
import Template from '../common/template'
import Placeholder from '../common/placeholder'

import styles from '../auction.module.scss'
import { PARTNER_SLUG } from '../../../constants/constant'

interface Props {
  artworkId: string
  userData: UserOffersQuery
  auction: AuctionByArtworkQuery['auction']
  loading?: boolean
  termsAndConditionsUrl: string
}

const validationSchema = object({
  firstName: string().min(3, 'Name is not valid').required('Name is required.'),
  email: EMAIL(),
  // phoneNumber: string().min(11, 'Phone number is not valid').required('Phone is required.'),
  // instagram: string()
  //   .min(3, 'Instagram handle is not valid')
  //   .matches(/^@/i, { message: 'Handle should starts with @' }),
}).required()

const initialValues = {
  firstName: '',
  email: '',
  phoneNumber: '',
  instagram: '',
  conditions: true,
}

const hideInstagram = [481, 12768, 12769, 12770, 12771]

const SubmitForGiveway: FC<Props> = ({
  artworkId,
  userData,
  auction,
  loading,
  termsAndConditionsUrl,
}) => {
  const endDate = auction?.endDate
  const startDate = auction?.startDate
  const purchaseStatus = auction?.purchaseStatus
  const currentBid = 0

  const {
    query: { slug },
  } = useQueryParams()
  const id = getIdFromSlug(slug)

  const { isEnd, willStart } = getUserBiddingStatusOnArtwork({
    purchaseStatus,
    currentBid,
    allOffers: userData?.currentUser?.offers?.edges,
    start: startDate,
    end: endDate,
  })

  const [submit, { data, loading: submitLoading }] = useMutation<
    SubmitForGiveawayMutation,
    SubmitForGiveawayMutationVariables
  >(SUBMIT_FOR_GIVEAWAY)

  const { handleSubmit, handleChange, handleBlur, values, errors, touched, isValid } = useFormik({
    initialValues,
    validationSchema,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onSubmit: async ({ conditions, ...v }) => {
      await submit({
        variables: {
          input: {
            artworkId,
            data: v,
            partnerSlug: PARTNER_SLUG,
          },
        },
      })
    },
  })

  const formattedErrors = useMemo(
    () =>
      Object.entries(errors)
        .filter(([field]) => touched[field] && errors[field])
        .map(([field, value]) => (
          <Typography.Text key={field} color="red" size="sm">
            {value}
          </Typography.Text>
        )),
    [touched, errors]
  )

  if (loading) {
    return <Placeholder />
  }

  if (isEnd || willStart) return null

  return (
    <Template iconName="dollar" title="Enter to win">
      <Typography.Text color="light-gray">
        Provide your details below for a chance to win. One entry per person allowed.
      </Typography.Text>
      {data?.submitForGiveaway.__typename === 'SubmitForGiveawaySuccess' ? (
        <Grid justifyContent="center">
          <Typography.Text>Thank you for your submission!</Typography.Text>
        </Grid>
      ) : (
        <form onSubmit={handleSubmit}>
          <Grid gap={18}>
            <Input
              id="firstName"
              name="firstName"
              value={values.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.firstName && errors.firstName}
              placeholder="Name"
              withError={false}
            />

            <Input
              id="email"
              name="email"
              type="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && errors.email}
              placeholder="Email Address"
              withError={false}
            />

            {/* <Grid gap={4}>
              <InternationalPhoneNumber
                id="phoneNumber"
                type="tel"
                name="phoneNumber"
                onBlur={handleBlur}
                value={values.phoneNumber}
                onChange={handleChange}
                error={touched.phoneNumber && errors.phoneNumber}
                withError={false}
              />
              <Typography.Text color="light-gray" size="sm">
                Granted will only use this information to contact winners & verify identification at
                check-in
              </Typography.Text>
            </Grid> */}
            {/*
            {!hideInstagram.includes(+id) && (
              <Grid gap={4}>
                <Input
                  id="instagram"
                  name="instagram"
                  value={values.instagram}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.instagram && errors.instagram}
                  placeholder="Instagram handle"
                  withError={false}
                />
                <Typography.Text color="light-gray" size="sm">
                  Get extra entries by following{' '}
                  <Link
                    rel="noopener noreferrer"
                    href={INSTAGRAM}
                    target="_blank"
                    color="g-green-darker"
                    size="sm"
                    external
                  >
                    @granted
                  </Link>{' '}
                  on Instagram and liking our giveaway post!
                </Typography.Text>
              </Grid>
            )} */}

            <Grid gap={24}>
              {data?.submitForGiveaway.__typename === 'SubmitForGiveawayFailure' ? (
                <Typography.Text color="red" center>
                  {data?.submitForGiveaway.errorMessage ||
                    'Some error occured. Please, try again later.'}
                </Typography.Text>
              ) : null}

              {!!formattedErrors.length && <Grid justifyContent="start">{formattedErrors}</Grid>}

              <Grid gap={8}>
                {termsAndConditionsUrl && (
                  <div className={styles['checkbox-wrapper']}>
                    <Checkbox
                      label={
                        <Typography.Text color="light-gray" size="sm">
                          I Agree to the{' '}
                          <Link
                            href={termsAndConditionsUrl}
                            rel="noopener noreferrer"
                            target="_blank"
                            color="g-green-darker"
                            size="sm"
                            external
                          >
                            Giveaway Terms and Conditions
                          </Link>{' '}
                          as set forth by Granted.
                        </Typography.Text>
                      }
                      checked={values.conditions}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      name="conditions"
                      id="conditions"
                    />
                  </div>
                )}

                <Button
                  disabled={submitLoading || !isValid || !values.conditions}
                  loading={submitLoading}
                  variant="primary"
                  type="submit"
                  fluid
                >
                  CONTINUE
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </form>
      )}
    </Template>
  )
}

export default SubmitForGiveway
